import { useLayoutEffect } from 'react';

interface ResizeObserverEntry {
  target: Element;
}

export declare class ResizeObserver {
  constructor(callback: ResizeObserverCallback);
  observe(target: Element): void;
  unobserve(target: Element): void;
  disconnect(): void;
}
/**
 * This is a hook for monitoring the size of an element, and
 * updating the element's size when it is appropriate
 * @param target , an element to watch at
 * @param callback , a callback, which gets a payload (width and height) and does the right thing (resizets the element)
 */
export function useSizeCallback(
  target: HTMLElement | null,
  callback: (payload: { width: number; height: number }) => void,
) {
  // this effect is used to mount the resize observer
  useLayoutEffect(() => {
    // if the target is null, we do not do anything
    if (!target) {
      return () => {
        //
      };
    }

    // the resize observer is created, and registers for every entry in the observer a function,
    // which monitors the sizes and calls the callback passed to any entry when resize was triggered
    const resizeObserver = new ResizeObserver((entries: ResizeObserverEntry[]) => {
      entries.forEach((entry) => {
        callback({
          width: entry.target.clientWidth,
          height: entry.target.clientHeight,
        });
      });
    });

    // observing the target and creating the cleaning function for this layout effect
    resizeObserver.observe(target);
    return () => {
      resizeObserver.unobserve(target);
      resizeObserver.disconnect();
    };
  }, [target, callback]);
}
